import { useCallback, useEffect, useRef, useState } from "react"

import { useTranslation } from "react-i18next"

import type { DirectorySearchResult } from "@/services/directory-search-service"

import { DirectorySearchResultItem } from "@/components/directory-search/directory-search-result-item"
import { Button } from "@/components/ui/button"
import { Spinner } from "@/components/ui/spinner"
import {
  DirectorySearchService,
  FILTERED_GROUP_ATTRIBUTE_PAGE_SIZE,
} from "@/services/directory-search-service"

type FilteredDirectoryListProps = {
  /** Graph user attribute to filter on (must match backend whitelist). */
  attribute: string
  /** Prefix value to match (backend uses startsWith). */
  value: string
  /** OIDs already selected — used to highlight matching rows. */
  selectedOids: string[]
  /** Single-click selection handler. */
  onSelect: (r: DirectorySearchResult) => void
  /** Optional bulk-add handler. When set, a header button appears. */
  onBulkAdd?: (results: DirectorySearchResult[]) => void
  /** Custom label for the bulk-add button. Defaults to the "Add all" i18n string. */
  bulkAddLabel?: string
  /** Scope user results to transitive members of this Azure AD group. */
  scopeGroup?: string
}

/**
 * Lists users matching a group-attribute filter (e.g. `department="Engineering"`).
 * Loads the first {@link FILTERED_GROUP_ATTRIBUTE_PAGE_SIZE} users, then fetches more
 * when the user scrolls near the bottom. Uses the same `DirectorySearchResultItem` as
 * the shared `DirectorySearch` for visual parity.
 */
export function FilteredDirectoryList({
  attribute,
  value,
  selectedOids,
  onSelect,
  onBulkAdd,
  bulkAddLabel,
  scopeGroup,
}: FilteredDirectoryListProps) {
  const { t } = useTranslation()
  const [results, setResults] = useState<DirectorySearchResult[]>([])
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [isInitialLoading, setIsInitialLoading] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const nextCursorRef = useRef<string | null>(null)
  const loadingMoreRef = useRef(false)

  useEffect(() => {
    nextCursorRef.current = nextCursor
  }, [nextCursor])

  // Debounce the value so we don't fire a Graph API call on every keystroke.
  const [debouncedValue, setDebouncedValue] = useState(value)
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), 300)
    return () => clearTimeout(timer)
  }, [value])

  useEffect(() => {
    const trimmed = debouncedValue.trim()
    if (!trimmed) {
      setResults([])
      setNextCursor(null)
      return
    }
    let cancelled = false
    setIsInitialLoading(true)
    setError(null)
    setNextCursor(null)
    DirectorySearchService.listByGroupAttributePage({
      attribute,
      value: trimmed,
      pageSize: FILTERED_GROUP_ATTRIBUTE_PAGE_SIZE,
      admin: true,
      scopeGroup,
    })
      .then(({ results: r, nextPageCursor }) => {
        if (!cancelled) {
          setResults(r)
          setNextCursor(nextPageCursor)
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : String(e))
          setResults([])
          setNextCursor(null)
        }
      })
      .finally(() => {
        if (!cancelled) setIsInitialLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [attribute, debouncedValue])

  const loadMore = useCallback(async () => {
    const trimmed = debouncedValue.trim()
    if (!trimmed || !nextCursorRef.current || loadingMoreRef.current) return
    loadingMoreRef.current = true
    setIsLoadingMore(true)
    try {
      const { results: more, nextPageCursor } =
        await DirectorySearchService.listByGroupAttributePage({
          attribute,
          value: trimmed,
          cursor: nextCursorRef.current,
          admin: true,
          scopeGroup,
        })
      setResults((prev) => {
        const seen = new Set(prev.map((u) => u.oid))
        const merged = [...prev]
        for (const r of more) {
          if (!seen.has(r.oid)) {
            seen.add(r.oid)
            merged.push(r)
          }
        }
        return merged
      })
      setNextCursor(nextPageCursor)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      loadingMoreRef.current = false
      setIsLoadingMore(false)
    }
  }, [attribute, debouncedValue])

  const onScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      if (!nextCursorRef.current || loadingMoreRef.current) return
      const el = e.currentTarget
      const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 100
      if (nearBottom) void loadMore()
    },
    [loadMore]
  )

  const selectedSet = new Set(selectedOids)

  if (isInitialLoading) {
    return (
      <div className="bg-card flex items-center justify-center rounded-md border py-6">
        <Spinner className="size-5" />
      </div>
    )
  }
  if (error) {
    return (
      <div className="bg-card text-destructive rounded-md border px-3 py-4 text-center text-sm">
        {error}
      </div>
    )
  }
  if (results.length === 0) {
    return (
      <div className="bg-card text-muted-foreground rounded-md border px-3 py-4 text-center text-sm">
        {t("adminDashboard.localAiLeads.filterNoResults", 'No users found matching "{{value}}".', {
          value,
        })}
      </div>
    )
  }

  const matchLabel =
    nextCursor != null
      ? t(
          "adminDashboard.localAiLeads.filterMatchCountScrollMore",
          "{{count}} loaded — scroll for more",
          {
            count: results.length,
          }
        )
      : t("adminDashboard.localAiLeads.filterMatchCount", "{{count}} users match.", {
          count: results.length,
        })

  return (
    <div className="bg-card rounded-md border">
      <div className="flex items-center justify-between gap-2 border-b px-3 py-2">
        <span className="text-muted-foreground min-w-0 text-xs">{matchLabel}</span>
        {onBulkAdd ? (
          <Button size="sm" variant="secondary" onClick={() => onBulkAdd(results)}>
            {bulkAddLabel ?? t("adminDashboard.localAiLeads.addAllVisible", "Add all")}
          </Button>
        ) : null}
      </div>
      <div className="h-[min(300px,_50dvh)] overflow-y-auto overscroll-contain" onScroll={onScroll}>
        <div className="p-1">
          {results.map((r) => (
            <DirectorySearchResultItem
              key={r.oid}
              groupAttribute={attribute}
              isSelected={selectedSet.has(r.oid)}
              result={r}
              onClick={() => onSelect(r)}
            />
          ))}
        </div>
        {isLoadingMore ? (
          <div className="text-muted-foreground flex justify-center py-2">
            <Spinner className="size-4" />
          </div>
        ) : null}
      </div>
    </div>
  )
}
